import { HN_SKJEMA_FRONTEND_RESOURCES } from 'src/constants/constants';
import { useResources } from 'src/resources';
import { useAppSelector } from 'src/store/reducers';
import { createUrlLink, openAttachmentInBrowser } from 'src/util/attacehmentUtils';

import type { Attachment, QuestionnaireResponse, QuestionnaireResponseItem } from 'fhir/r4';
import type { HNSkjemaFrontend } from 'src/types/Resources';

import type { UploadFile } from '@helsenorge/file-upload/components/file-upload';
import { link } from '@helsenorge/framework-utils/hn-proxy-service';
import type { TextMessage } from '@helsenorge/refero';

type UseAttachmentUtilsReturn = {
  generateAttachmentLink: (fileId: string) => string;
  openAttachment: (fileId: string) => void;
  uploadAttachment: (
    files: UploadFile[],
    onSuccess: (attachment: Attachment) => void,
    onError: (errormessage: TextMessage | null) => void
  ) => void;
  deleteAttachment: (fileId: string, onSuccess: () => void) => void;
  getAttachmentLink: (fileId: string) => string;
};

const useAttachmentUtils = (): UseAttachmentUtilsReturn => {
  const qr = useAppSelector(state => state.refero.form.FormData.Content);
  const resources = useResources<HNSkjemaFrontend>(HN_SKJEMA_FRONTEND_RESOURCES);
  const generateAttachmentLink = (fileId: string): string => {
    return link('skjemainternal', 'api/v1/Attachment', {
      id: fileId,
    });
  };

  const openAttachment = (fileId: string): void => {
    if (qr) {
      const item = findAllValueAttachments(qr, fileId);
      if (item.length > 0 && item[0].data && item[0].data?.length > 10) {
        openAttachmentInBrowser(item[0]);
      } else {
        window.open(generateAttachmentLink(fileId), '_blank');
      }
    } else {
      window.open(generateAttachmentLink(fileId), '_blank');
    }
  };

  const uploadAttachment = (
    files: UploadFile[],
    onSuccess: (attachment: Attachment) => void,
    onError: (errormessage: TextMessage | null) => void
  ): void => {
    const reader = new FileReader();
    reader.onload = (): void => {
      const dataString = reader.result?.toString() || '';
      const attachment: Attachment = {
        id: files[0].id,
        title: files[0].name,
        url: files[0].id,
        data: dataString.substring(dataString.indexOf(',') + 1),
        contentType: files[0].type,
      };

      onSuccess(attachment);
    };
    reader.onerror = (): void => {
      onError({ Title: resources?.uploadFileError || '', Body: '' });
    };

    reader.readAsDataURL(files[0]);
  };
  const deleteAttachment = (_fileId: string, onSuccess: () => void): void => {
    onSuccess();
  };
  function findAllValueAttachments(qr: QuestionnaireResponse, fileId: string): Attachment[] {
    const attachments: Attachment[] = [];

    function walk(items?: QuestionnaireResponseItem[]): void {
      if (!items) return;

      for (const item of items) {
        if (item.answer) {
          for (const ans of item.answer) {
            if (ans.valueAttachment && ans.valueAttachment.id === fileId) {
              attachments.push(ans.valueAttachment);
            }

            if (ans.item) walk(ans.item);
          }
        }

        if (item.item) {
          walk(item.item);
        }
      }
    }

    walk(qr.item);

    return attachments;
  }
  const getAttachmentLink = (fileId: string): string => {
    if (qr) {
      const item = findAllValueAttachments(qr, fileId);
      if (item.length > 0 && item[0].data && item[0].data?.length > 10) {
        return createUrlLink(item[0]);
      }
    }
    return generateAttachmentLink(fileId);
  };
  return {
    generateAttachmentLink,
    openAttachment,
    uploadAttachment,
    deleteAttachment,
    getAttachmentLink,
  };
};
export default useAttachmentUtils;
